/**
 * Shared feature addition validation checks.
 *
 * These are the canonical validation functions used by BOTH the vehicle feature
 * addition flow (AddRemoveFeatureSearchAndResultsTable) and the collection feature
 * addition flow (FeatureCollectionDetails).
 *
 * IMPORTANT: The vehicle flow is the reference implementation. Any change to the
 * logic here affects vehicles. The collection flow adopts these checks as-is,
 * with context-appropriate parameters supplied by the caller.
 *
 * Each function returns null when the feature passes the check, or an error
 * string when it fails. This matches the existing vehicle validation convention.
 */

import { pathOr } from 'ramda'
import strings from 'utils/strings'
import {
  CHECPLUS_MODULE_TYPES,
  VALID_STATUSES,
  MAX_TIMERS_PER_XMC,
  MAX_LOGICSETS_PER_XMC,
  MAX_LOGICSETS_PER_ASAM,
  MAX_TIMERS_PER_ASAM,
  MAX_CAN_INPUTS,
  MAX_SWITCH_INDICATORS,
  AL,
  PWM,
} from 'utils/constants'
import { is43NAlignedWith42N } from 'utils/pinRestrictions'

const { XMC, ASAM } = CHECPLUS_MODULE_TYPES

const isValidPositiveCapacity = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0

const isSameFeature = (left, right) =>
  left.uri && right.uri
    ? left.uri === right.uri
    : String(left.id) === String(right.id)

export const getResourceModuleName = (moduleName) => moduleName

const getCollectionResourceModuleName = (moduleName) =>
  (moduleName || '').replace(/\s/g, '')

const getTotalsWithSelectedFeatures = (
  totalsAndLimits,
  selectedFeatures,
  modules,
  record
) =>
  selectedFeatures.reduce((totals, selectedFeature) => {
    if (isSameFeature(selectedFeature, record)) return totals

    const moduleName = modules
      ? getResourceModuleName(selectedFeature.moduleName, modules)
      : getCollectionResourceModuleName(selectedFeature.moduleName)
    const moduleTotals = totals[moduleName] || {}
    return {
      ...totals,
      [moduleName]: {
        ...moduleTotals,
        timerCount:
          (moduleTotals.timerCount || 0) +
          (selectedFeature.timerQueryCount || 0),
        logicCount:
          (moduleTotals.logicCount || 0) +
          (selectedFeature.complexQueryCount || 0),
      },
    }
  }, totalsAndLimits)

// ---------------------------------------------------------------------------
// Check 1 – Authorization (vehicle-only: caller passes null to skip)
// ---------------------------------------------------------------------------

/**
 * Verify the user is authorized to add a feature on this module.
 *
 * @param {object} record - Feature record
 * @param {string[]|null} authenticatedModuleNames - Modules the user may update,
 *   or null/undefined to skip this check (collection context).
 * @returns {string|null}
 */
export const checkNotAuthorized = (record, authenticatedModuleNames) => {
  if (!authenticatedModuleNames) return null
  const { moduleName } = record
  if (!authenticatedModuleNames.includes(moduleName)) {
    return strings.not_authorized_to_add_module
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 2 – Module availability (vehicle: must be present; collection: skipped)
// ---------------------------------------------------------------------------

/**
 * Verify the feature's module is physically present on the vehicle.
 * Pass null for `modules` to skip this check (collection context uses
 * `checkIsEligibleEcu` instead).
 *
 * @param {object} record - Feature record
 * @param {string[]|null} modules - Module names on the vehicle, or null to skip.
 * @param {function} displayModuleName - Formatter for user-visible module name.
 * @returns {string|null}
 */
export const checkModuleNotOnVehicle = (record, modules, displayModuleName) => {
  if (!modules) return null
  if (!modules.includes(record.moduleName)) {
    return strings.module_not_on_vehicle(displayModuleName(record.moduleName))
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 3 – ECU eligibility (collection-only: caller passes null to skip)
// ---------------------------------------------------------------------------

const ALLOWED_COLLECTION_ECUS_NORMALIZED = ['XMC3', 'XMC4']

/**
 * Verify the feature is on an XMC 3 or XMC 4 ECU, as required for collections.
 * Pass null for `allowedEcus` to skip this check (vehicle context uses
 * `checkModuleNotOnVehicle` instead).
 *
 * @param {object} record - Feature record
 * @param {string[]|null} allowedEcus - Normalized allowed ECU names, or null to skip.
 * @returns {string|null}
 */
export const checkIsEligibleEcu = (record, allowedEcus) => {
  if (!allowedEcus) return null
  const normalized = (record.moduleName || '').replace(/\s/g, '')
  if (!allowedEcus.includes(normalized)) {
    return strings.feature_ecu_not_eligible
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 4 – Restricted module (ASAM / XMC 1 / XMC 2)
// ---------------------------------------------------------------------------

/**
 * Verify the feature is not on a factory-controlled (restricted) module.
 *
 * @param {object} record - Feature record
 * @param {string[]} restrictedModules - List of restricted module names.
 * @returns {string|null}
 */
export const checkRestrictedModule = (record, restrictedModules) => {
  if (restrictedModules.includes(record.moduleName)) {
    return strings.feature_on_restricted_module
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 5 – Already present
// ---------------------------------------------------------------------------

/**
 * Verify the feature is not already present in the target (vehicle or collection).
 *
 * For vehicles: pass the current vehicle features array.
 * For collections: pass the working set filtered to exclude features marked for deletion.
 *
 * @param {object} record - Feature record
 * @param {object[]} existingFeatures - Features already in the target.
 * @param {string} errorMessage - Context-appropriate error string.
 * @returns {string|null}
 */
export const checkAlreadyPresent = (record, existingFeatures, errorMessage) => {
  if (
    existingFeatures.some(
      (feature) =>
        feature.partNumber === record.partNumber ||
        String(feature.id) === String(record.id)
    )
  ) {
    return errorMessage
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 6 – Feature status
// ---------------------------------------------------------------------------

/**
 * Verify the feature has an allowed status.
 *
 * Vehicles allow: Active, Test, Verified  (VALID_STATUSES)
 * Collections allow: Active only
 *
 * Pass the appropriate list via `allowedStatuses`.
 *
 * @param {object} record - Feature record
 * @param {string[]} allowedStatuses - Allowed status strings (case-insensitive comparison).
 * @param {string} errorMessage - Context-appropriate error string.
 * @returns {string|null}
 */
export const checkStatus = (record, allowedStatuses, errorMessage) => {
  const lowerAllowed = allowedStatuses.map((s) => s.toLowerCase())
  if (!lowerAllowed.includes((record.status || '').toLowerCase())) {
    return errorMessage
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 7 – Backend compatibility flag
// ---------------------------------------------------------------------------

/**
 * Verify the backend has not flagged this feature as incompatible.
 *
 * For vehicles: `isCompatible` / `incompatibilityReason` are already populated
 *   on the search result by the existing search API.
 * For collections: these fields will be populated by the new
 *   POST /groups/{id}/features/compatible endpoint once available.
 *   Until then the fields are absent and the check passes (returns null).
 *
 * @param {object} record - Feature record with optional isCompatible / incompatibilityReason
 * @returns {string|null}
 */
export const checkIsIncompatible = (record) => {
  const { isCompatible, incompatibilityReason } = record
  // If isCompatible is explicitly false, the feature is blocked.
  // If absent (undefined), treat as compatible (backward-compatible default).
  if (isCompatible === false)
    return incompatibilityReason || strings.feature_incompatible
  return null
}

// ---------------------------------------------------------------------------
// Check 8 – ASAM timer limit
// ---------------------------------------------------------------------------

/**
 * Verify adding this ASAM feature would not exceed the ASAM timer limit.
 *
 * @param {object} record - Feature record
 * @param {object} totalsAndLimits - Per-module resource totals and maxima.
 * @returns {string|null}
 */
export const checkExceedsMaxTimersASAM = (record, totalsAndLimits) => {
  if (record.moduleType === ASAM) {
    const timerCount = pathOr(0, [ASAM, 'timerCount'], totalsAndLimits)
    const maxTimer = pathOr(
      MAX_TIMERS_PER_ASAM,
      [ASAM, 'maxTimer'],
      totalsAndLimits
    )
    if (timerCount + record.timerQueryCount > maxTimer) {
      return strings.exceeds_maximum_timers_asam
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 9 – ASAM logic set limit
// ---------------------------------------------------------------------------

/**
 * Verify adding this ASAM feature would not exceed the ASAM logic set limit.
 *
 * @param {object} record - Feature record
 * @param {object} totalsAndLimits - Per-module resource totals and maxima.
 * @returns {string|null}
 */
export const checkExceedsMaxLogicSetsASAM = (record, totalsAndLimits) => {
  if (record.moduleType === ASAM) {
    const logicCount = pathOr(0, [ASAM, 'logicCount'], totalsAndLimits)
    const maxLogic = pathOr(
      MAX_LOGICSETS_PER_ASAM,
      [ASAM, 'maxLogic'],
      totalsAndLimits
    )
    if (logicCount + record.complexQueryCount > maxLogic) {
      return strings.exceeds_maximum_logicsets_asam
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 10 – XMC timer limit
// ---------------------------------------------------------------------------

/**
 * Verify adding this XMC feature would not exceed the per-module timer limit.
 *
 * @param {object} record - Feature record
 * @param {object} totalsAndLimits - Per-module resource totals and maxima.
 * @returns {string|null}
 */
export const checkExceedsMaxTimersXMC = (
  record,
  totalsAndLimits,
  modules = []
) => {
  if (record.moduleType === XMC) {
    const moduleName = getResourceModuleName(record.moduleName, modules)
    const timerCount = pathOr(0, [moduleName, 'timerCount'], totalsAndLimits)
    const configuredMaxTimer = pathOr(
      null,
      [moduleName, 'maxTimer'],
      totalsAndLimits
    )
    const maxTimer = isValidPositiveCapacity(configuredMaxTimer)
      ? configuredMaxTimer
      : MAX_TIMERS_PER_XMC
    if (timerCount + record.timerQueryCount > maxTimer) {
      return strings.exceeds_maximum_timers_xmc
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 11 – XMC logic set limit
// ---------------------------------------------------------------------------

/**
 * Verify adding this XMC feature would not exceed the per-module logic set limit.
 *
 * @param {object} record - Feature record
 * @param {object} totalsAndLimits - Per-module resource totals and maxima.
 * @returns {string|null}
 */
export const checkExceedsMaxLogicSetsXMC = (
  record,
  totalsAndLimits,
  modules = []
) => {
  if (record.moduleType === XMC) {
    const moduleName = getResourceModuleName(record.moduleName, modules)
    const logicCount = pathOr(0, [moduleName, 'logicCount'], totalsAndLimits)
    const configuredMaxLogic = pathOr(
      null,
      [moduleName, 'maxLogic'],
      totalsAndLimits
    )
    const maxLogic = isValidPositiveCapacity(configuredMaxLogic)
      ? configuredMaxLogic
      : MAX_LOGICSETS_PER_XMC
    if (logicCount + record.complexQueryCount > maxLogic) {
      return strings.exceeds_maximum_logicsets_xmc
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Check 12 – CAN inputs limit
// ---------------------------------------------------------------------------

/**
 * Verify adding this feature would not exceed the per-module CAN inputs limit.
 * Deduplicates CAN inputs that are already counted on the module.
 *
 * @param {object} record - Feature record
 * @param {object} canInputsByModule - Per-module CAN input data.
 * @returns {string|null}
 */
export const checkExceedsMaxCanInputsCount = (record, canInputsByModule) => {
  if (!canInputsByModule[record.moduleName]) return null
  const { canInputs = [] } = canInputsByModule[record.moduleName]
  const canInputsFromRecord = record.canInputs || []
  const canInputsLengthFromRecordWithoutDuplicates = canInputs.reduce(
    (acc, curr) => {
      if (canInputsFromRecord.find((item) => item === curr)) {
        return acc - 1
      }
      return acc
    },
    canInputsFromRecord.length
  )
  const canInputsCountFromModule = canInputs.length
  const maxCount = pathOr(
    MAX_CAN_INPUTS,
    [record.moduleName, 'maxCount'],
    canInputsByModule
  )
  return canInputsLengthFromRecordWithoutDuplicates + canInputsCountFromModule >
    maxCount
    ? strings.exceeds_maximum_can_inputs_xmc(record.moduleType)
    : null
}

// ---------------------------------------------------------------------------
// Check 13 – Switch indicator limit
// ---------------------------------------------------------------------------

/**
 * Verify adding this feature would not exceed the switch indicator limit.
 * Deduplicates switch indicators that are already counted on the module.
 *
 * @param {object} record - Feature record
 * @param {object} switchIndicatorsByModule - Per-module switch indicator data.
 * @returns {string|null}
 */
export const checkExceedsMaxSwitchCount = (
  record,
  switchIndicatorsByModule
) => {
  const { switches = [] } = switchIndicatorsByModule[record.moduleName] || {}
  const switchesFromRecord = record.switchIndicators || []
  const switchesLengthIncludingSwitchesFromRecord = new Set(
    switchesFromRecord.concat(switches)
  ).size
  const maxCount = MAX_SWITCH_INDICATORS
  return maxCount && switchesLengthIncludingSwitchesFromRecord > maxCount
    ? strings.exceeds_maximum_switch_indicators_xmc(record.moduleType)
    : null
}

// ---------------------------------------------------------------------------
// Check 14 – 2A VPDM output restriction
// ---------------------------------------------------------------------------

/**
 * Verify the feature does not have 2A outputs with incompatible polarity
 * for the target vehicle/collection's QuickFit Sales Code.
 *
 * For vehicles: pass the vehicle's db35mCode and buildDate.
 * For collections: pass the collection's QuickFit Sales Code (mdlDbCd) and
 *   buildDate. Both fields are stored on the collection and returned by the
 *   API (GET /groups/{id}). When searching for features to add in edit mode,
 *   mdlDbCd, buildDate, and vehicleSubType are passed in addToSearchRequestBody
 *   so the backend evaluates IsFeatureVpdmCompatible on search results (Bug 501357).
 *
 * @param {object} record - Feature record
 * @param {string|null} db35mCode - The 35M QuickFit Sales Code of the vehicle or collection.
 * @param {string|null} buildDate - ISO date string, or null if not yet available.
 * @returns {string|null}
 */
export const checkHas2aVpdmRestriction = (record, db35mCode, buildDate) => {
  // Skip if the backend already flagged this feature as incompatible
  // to avoid showing duplicate restriction messages in the tooltip
  if (record.isCompatible === false) return null

  // Only applies to post-cutoff 43N with 35M-011, 35M-012, or 35M-015
  if (!is43NAlignedWith42N(db35mCode, buildDate)) return null

  // isFeatureVpdmCompatible: true means the feature has 2A output pins on
  // VPDM-routed pins. Only block if those outputs use PWM or Active Low (AL)
  // polarity. High Side (AH) polarity features are always allowed.
  // Note: if functions data is not available from the search API,
  // hasNonHighSideOutput is undefined (falsy) — feature is allowed by default.
  if (record.isFeatureVpdmCompatible === true) {
    const hasNonHighSideOutput = record.functions?.some((fn) =>
      fn.outputMap?.outputs?.some(
        (output) => output.polarity === AL || output.polarity === PWM
      )
    )
    if (hasNonHighSideOutput) {
      return strings.add_feature_2a_output(db35mCode)
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// Convenience: build the vehicle validation pipeline
// ---------------------------------------------------------------------------

/**
 * Build the ordered array of bound validation functions for the vehicle context.
 * The order and logic is identical to the original inline definitions.
 *
 * @param {object} ctx
 * @param {string[]} ctx.authenticatedModuleNames
 * @param {string[]} ctx.modules
 * @param {function} ctx.displayModuleName
 * @param {string[]} ctx.restrictedModules
 * @param {object[]} ctx.features - Features currently on the vehicle
 * @param {object} ctx.totalsAndLimits
 * @param {object} ctx.canInputsByModule
 * @param {object} ctx.switchIndicatorsByModule
 * @param {string|null} ctx.db35mCode
 * @param {string|null} ctx.buildDate
 * @returns {Array<function(record): string|null>}
 */
export const buildVehicleValidationFns = ({
  authenticatedModuleNames,
  modules,
  displayModuleName,
  restrictedModules,
  features,
  totalsAndLimits,
  selectedFeatures = [],
  canInputsByModule,
  switchIndicatorsByModule,
  db35mCode,
  buildDate,
}) => [
  (record) => checkNotAuthorized(record, authenticatedModuleNames),
  (record) => checkModuleNotOnVehicle(record, modules, displayModuleName),
  (record) =>
    checkExceedsMaxTimersASAM(
      record,
      getTotalsWithSelectedFeatures(
        totalsAndLimits,
        selectedFeatures,
        modules,
        record
      )
    ),
  (record) =>
    checkExceedsMaxLogicSetsASAM(
      record,
      getTotalsWithSelectedFeatures(
        totalsAndLimits,
        selectedFeatures,
        modules,
        record
      )
    ),
  (record) =>
    checkExceedsMaxTimersXMC(
      record,
      getTotalsWithSelectedFeatures(
        totalsAndLimits,
        selectedFeatures,
        modules,
        record
      ),
      modules
    ),
  (record) =>
    checkExceedsMaxLogicSetsXMC(
      record,
      getTotalsWithSelectedFeatures(
        totalsAndLimits,
        selectedFeatures,
        modules,
        record
      ),
      modules
    ),
  (record) =>
    checkAlreadyPresent(record, features, strings.feature_already_on_vehicle),
  (record) =>
    checkStatus(
      record,
      VALID_STATUSES,
      strings.feature_status_invalid_for_vehicle_addition
    ),
  (record) => checkIsIncompatible(record),
  (record) => checkExceedsMaxSwitchCount(record, switchIndicatorsByModule),
  (record) => checkExceedsMaxCanInputsCount(record, canInputsByModule),
  (record) => checkHas2aVpdmRestriction(record, db35mCode, buildDate),
]

// ---------------------------------------------------------------------------
// Convenience: build the collection validation pipeline
// ---------------------------------------------------------------------------

/**
 * Build the ordered array of bound validation functions for the collection context.
 *
 * Note: `notAuthorized` and `moduleNotOnVehicle` are skipped (collection auth is
 * handled at the page level; collections don't have physical modules).
 * `isEligibleEcu` and `isOnRestrictedModule` replace them.
 *
 * Status is restricted to Active-only for collections (user decision).
 *
 * The VPDM check uses the collection's QuickFit Sales Code (mdlDbCd) and buildDate.
 * Both fields are stored on the collection and returned by the API. When searching
 * for features to add in edit mode, these values are passed via addToSearchRequestBody
 * so the backend evaluates IsFeatureVpdmCompatible on search results (Bug 501357).
 *
 * @param {object} ctx
 * @param {string[]} ctx.restrictedModules
 * @param {object[]} ctx.workingSet - Current working set (already filtered: no deleted)
 * @param {object[]} ctx.selectedFeatures - Candidates currently selected in search
 * @param {object} ctx.totalsAndLimits - Per-module resource totals & maxima
 * @param {object} ctx.canInputsByModule
 * @param {object} ctx.switchIndicatorsByModule
 * @param {string|null} ctx.mdlDbCd - Collection's QuickFit Sales Code
 * @param {string|null} ctx.buildDate - Collection's build date (from GET /groups/{id})
 * @returns {Array<function(record): string|null>}
 */
export const buildCollectionValidationFns = ({
  restrictedModules,
  workingSet,
  totalsAndLimits,
  selectedFeatures = [],
  canInputsByModule, // retained in signature for call-site compatibility
  switchIndicatorsByModule, // retained in signature for call-site compatibility
  mdlDbCd,
  buildDate,
}) => [
  (record) => checkIsEligibleEcu(record, ALLOWED_COLLECTION_ECUS_NORMALIZED),
  (record) => checkRestrictedModule(record, restrictedModules),
  (record) =>
    checkAlreadyPresent(
      record,
      workingSet,
      strings.feature_already_in_collection
    ),
  (record) => checkStatus(record, ['Active'], strings.feature_status_not_valid),
  (record) => checkIsIncompatible(record),
  (record) => {
    const moduleName = getCollectionResourceModuleName(record.moduleName)
    const totals =
      getTotalsWithSelectedFeatures(
        totalsAndLimits,
        selectedFeatures,
        null,
        record
      )[moduleName] || {}
    const maxTimer = isValidPositiveCapacity(totals.maxTimer)
      ? totals.maxTimer
      : MAX_TIMERS_PER_XMC
    return (totals.timerCount || 0) + (record.timerQueryCount || 0) > maxTimer
      ? strings.exceeds_maximum_timers_xmc
      : null
  },
  (record) => {
    const moduleName = getCollectionResourceModuleName(record.moduleName)
    const totals =
      getTotalsWithSelectedFeatures(
        totalsAndLimits,
        selectedFeatures,
        null,
        record
      )[moduleName] || {}
    const maxLogic = isValidPositiveCapacity(totals.maxLogic)
      ? totals.maxLogic
      : MAX_LOGICSETS_PER_XMC
    return (totals.logicCount || 0) + (record.complexQueryCount || 0) > maxLogic
      ? strings.exceeds_maximum_logicsets_xmc
      : null
  },
  (record) => checkHas2aVpdmRestriction(record, mdlDbCd, buildDate),
]
